import { createPortal } from 'react-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { UnifiedSearchableSelect } from '@tpct/admin-ui';
import type { User } from '@/users/src/types/user';
import { __ } from '@/hub-shared/utils/i18n';
import { updateUser, handleApiError } from '@/users/src/utils/api';
import { buildRoleSelectOptions } from '@/users/src/utils/roleSelectOptions';
import { companyIdsForUserUpdatePayload } from '@/users/src/utils/companyIdsForUpdate';

type ChangeUserRoleModalProps = {
  open: boolean;
  user: User;
  bootstrapKey?: string;
  canManageUsers?: boolean;
  onClose: () => void;
  onSuccess: (updated: User) => void;
};

const verifyRoleApplied = (
  updated: User,
  expectedPrimary: string,
  previousPrimary: string,
): boolean => {
  if (updated.role !== expectedPrimary) {
    return false;
  }
  if (previousPrimary === '' || expectedPrimary === previousPrimary) {
    return true;
  }
  return !updated.roles.includes(previousPrimary);
};

const ChangeUserRoleModal = ({
  open,
  user,
  bootstrapKey = 'AccountingHubUsersAdmin',
  canManageUsers = true,
  onClose,
  onSuccess,
}: ChangeUserRoleModalProps) => {
  const roleOptions = useMemo(() => buildRoleSelectOptions(bootstrapKey), [bootstrapKey]);
  const [selectedRole, setSelectedRole] = useState(user.role);
  const [error, setError] = useState('');
  const [savePhase, setSavePhase] = useState<'idle' | 'saving'>('idle');
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setSelectedRole(user.role);
    setError('');
    setSavePhase('idle');
  }, [open, user.id, user.role]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  if (!open || !canManageUsers) {
    return null;
  }

  const companyIdsSnapshot = companyIdsForUserUpdatePayload(user);
  const previousPrimary = user.role;
  const selectionChanged = selectedRole !== previousPrimary;
  const saving = savePhase === 'saving';
  const canSave = selectionChanged && !saving && roleOptions.some((o) => o.value === selectedRole && !o.disabled);

  const handleSave = async () => {
    if (!canSave) {
      return;
    }

    setSavePhase('saving');
    setError('');

    const nextPrimary = selectedRole;

    try {
      const updated = await updateUser(
        user.id,
        {
          role: nextPrimary,
          company_ids: companyIdsSnapshot,
        },
        bootstrapKey,
      );

      if (!verifyRoleApplied(updated, nextPrimary, previousPrimary)) {
        const reverted = await updateUser(
          user.id,
          {
            role: previousPrimary,
            company_ids: companyIdsSnapshot,
          },
          bootstrapKey,
        );

        if (!verifyRoleApplied(reverted, previousPrimary, nextPrimary)) {
          setError(
            __(
              'La réponse du serveur est incohérente et le rôle n\'a pas pu être rétabli automatiquement. Contactez le support.',
            ),
          );
          return;
        }

        onSuccess(reverted);
        setError(
          __(
            'Le serveur n\'a pas confirmé le nouveau rôle. L\'ancien rôle a été rétabli.',
          ),
        );
        return;
      }

      onSuccess(updated);
      onClose();
    } catch (err: unknown) {
      setError(handleApiError(err));
    } finally {
      setSavePhase('idle');
    }
  };

  const titleId = `change-user-role-modal-title-${user.id}`;

  const handleModalInnerClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  return createPortal(
    <div className="thn-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="thn-modal thn-modal--small change-user-role-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={handleModalInnerClick}
      >
        <button
          type="button"
          className="thn-modal__close"
          onClick={onClose}
          aria-label={__('Fermer')}
          disabled={saving}
        >
          <span aria-hidden>×</span>
        </button>

        <div className="thn-modal__header">
          <h3 id={titleId}>{__('Changer le rôle')}</h3>
        </div>

        <div className="thn-modal__body">
          <div className="form-field">
            <label htmlFor={`change-user-role-select-${user.id}`}>
              {__('Rôle')}
            </label>
            <UnifiedSearchableSelect
              id={`change-user-role-select-${user.id}`}
              options={roleOptions}
              value={selectedRole}
              onChange={(value) => {
                setSelectedRole(value);
              }}
              disabled={saving}
              searchable={false}
              placeholder={__('Sélectionner un rôle')}
            />
          </div>

          {error !== '' ? (
            <div className="notice notice-error" role="alert">
              <p>{error}</p>
            </div>
          ) : null}
        </div>

        <div className="thn-modal__footer">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            {__('Annuler')}
          </button>
          <button type="button" className="btn-primary" onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? __('Enregistrement…') : __('Enregistrer')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ChangeUserRoleModal;
